import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/ui/password-input';
import { Loader2, Mail, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = await login(email, password);
      toast.success('Login realizado com sucesso!');
      
      // Redirect based on user role
      const userRole = userData?.role;
      if (userRole === 'superadmin' || userRole === 'super_admin') {
        navigate('/superadmin/dashboard');
      } else if (userRole === 'funcionario' || userRole === 'employee') {
        navigate('/employee/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Digite seu email');
      return;
    }
    
    setSendingReset(true);
    
    try {
      const response = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      
      if (response.ok) {
        setResetSent(true);
        toast.success('Se o email existir, você receberá um link de recuperação');
      } else {
        toast.error('Erro ao enviar email de recuperação');
      }
    } catch (error) {
      toast.error('Erro ao conectar com o servidor');
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left Side - Branding with Swirl Pattern */}
      <div className="hidden lg:flex flex-col justify-between items-center relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #d5e8e5 0%, #c5dcd8 50%, #b5d0cc 100%)'
        }}
      >
        {/* Swirl Pattern Background */}
        <div 
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: 'url(/swirl-pattern.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'overlay'
          }}
        />
        
        {/* Top Tagline */}
        <div className="relative z-10 pt-12 text-center px-8">
          <h2 className="text-xl md:text-2xl text-[#1e3a5f] font-light leading-relaxed">
            Gestão de Riscos Psicossociais - SST
          </h2>
          <p className="text-lg text-[#1e3a5f] font-medium mt-1">
            em conformidade com NR1/GRO
          </p>
        </div>
        
        {/* Center Logo */}
        <div className="relative z-10 flex-1 flex items-center justify-center">
          <img 
            src="/logo-full.png" 
            alt="CERP Brasil NR1" 
            className="w-80 h-auto drop-shadow-lg"
          />
        </div>
        
        {/* Bottom Feature Pills */}
        <div className="relative z-10 pb-12 px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {['Gestão de Riscos', 'Avaliações PSI', 'Treinamentos', 'Relatórios PGR'].map((feature) => (
              <span 
                key={feature}
                className="px-5 py-2.5 bg-white/80 backdrop-blur-sm rounded-full text-sm font-medium text-[#1e3a5f] shadow-sm border border-white/50 hover:bg-white transition-colors cursor-default"
              >
                {feature}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex flex-col items-center justify-center p-6 md:p-8 bg-white min-h-screen">
        {/* Mobile Logo */}
        <div className="lg:hidden mb-8">
          <img 
            src="/logo-full.png" 
            alt="CERP Brasil NR1" 
            className="h-24 w-auto"
          />
        </div>
        
        <div className="w-full max-w-sm">
          {/* Avatar/Icon */}
          <div className="flex justify-center mb-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#e8f4f3] to-[#d5ebe8] flex items-center justify-center shadow-inner">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#b8d8d3] to-[#9cc5bf]"></div>
            </div>
          </div>
          
          {/* Form Card */}
          <div data-testid="login-card">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-semibold text-[#1e3a5f] mb-2">
                {showForgotPassword ? 'Recuperar Senha' : 'Bem Vindo ao CERP Brasil'}
              </h2>
              <p className="text-gray-500 text-sm">
                {showForgotPassword 
                  ? 'Digite seu email para receber o link de recuperação' 
                  : 'Acesse sua conta para continuar'}
              </p>
            </div>

            {showForgotPassword ? (
              /* Forgot Password Form */
              resetSent ? (
                <div className="text-center space-y-4">
                  <div className="mx-auto w-16 h-16 bg-[#e8f4fc] rounded-full flex items-center justify-center">
                    <Mail className="h-8 w-8 text-[#0d9488]" />
                  </div>
                  <p className="text-gray-500">
                    Se o email <strong className="text-[#1e3a5f]">{forgotEmail}</strong> estiver cadastrado, você receberá um link para redefinir sua senha.
                  </p>
                  <p className="text-sm text-gray-400">
                    Verifique também sua caixa de spam.
                  </p>
                  <Button 
                    variant="outline" 
                    className="w-full mt-4 border-[#0d9488] text-[#0d9488] hover:bg-[#0d9488] hover:text-white rounded-lg"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetSent(false);
                      setForgotEmail('');
                    }}
                  >
                    Voltar para Login
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="forgotEmail" className="text-gray-700">Email</Label>
                    <Input
                      id="forgotEmail"
                      type="email"
                      placeholder="seu@email.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="h-11 rounded-lg border-gray-200 focus:border-[#0d9488] focus:ring-[#0d9488]"
                      required
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={sendingReset}
                    className="w-full h-11 bg-[#0d9488] hover:bg-[#0b7d73] text-white rounded-lg font-medium"
                  >
                    {sendingReset ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      'Enviar Link de Recuperação'
                    )}
                  </Button>
                  
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(false)}
                    className="w-full text-sm text-gray-500 hover:text-[#0d9488] transition-colors"
                  >
                    Voltar para Login
                  </button>
                </form>
              )
            ) : (
              /* Login Form */
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-medium">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11 rounded-lg border-gray-200 focus:border-[#0d9488] focus:ring-[#0d9488]"
                    required
                    data-testid="login-email"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-gray-700 font-medium">Senha</Label>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-[#0d9488] hover:text-[#0b7d73] font-medium transition-colors"
                    >
                      Esqueceu a senha?
                    </button>
                  </div>
                  <PasswordInput
                    id="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 rounded-lg border-gray-200 focus:border-[#0d9488] focus:ring-[#0d9488]"
                    required
                    data-testid="login-password"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-[#0d9488] hover:bg-[#0b7d73] text-white rounded-lg font-medium group"
                  data-testid="login-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Entrando...
                    </>
                  ) : (
                    <>
                      Entrar
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </Button>

                <p className="text-center text-sm text-gray-500 pt-2">
                  Não tem uma conta?{' '}
                  <Link to="/register" className="text-[#0d9488] hover:text-[#0b7d73] font-medium transition-colors">
                    Cadastre-se
                  </Link>
                </p>
              </form>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="mt-auto pt-8 text-center">
          <p className="text-xs text-gray-400">
            © 2026 Ogunhê Desenvolvimento de Sistemas Ltda. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
